import type { SxProps } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { ACCENT_STRIP_HEIGHT, ALERT_CORAL, MOTION, NEUTRAL, STAT_TILE, STATUS, SURFACE } from "../../../theme/tokens";
import type { ClickableStatState } from "./ClickableStatTile";

export const STATE_ACCENT: Record<Exclude<ClickableStatState, "default">, string> = {
	matching: STATUS.SUCCESS.main,
	"not-matching": ALERT_CORAL,
};

type TileStyle = {
	clickable: boolean;
	/** The active state's colour; undefined while the tile isn't filtering. */
	accent: string | undefined;
	dashed: boolean;
	accentColor: string | undefined;
	entranceIndex: number | undefined;
};

/** The `ClickableStatTile` surface: its edge, active rail, hover lift, press, focus ring and optional entrance. */
export const clickableStatTileSx =
	({ clickable, accent, dashed, accentColor, entranceIndex }: TileStyle): SxProps<Theme> =>
	(theme) => ({
		// Override the default `width: fit-content` so tiles grow to fill the responsive grid.
		width: "auto",
		flex: "1 1 0",
		minWidth: STAT_TILE.MIN_WIDTH,
		minHeight: STAT_TILE.MIN_HEIGHT,
		position: "relative",
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		padding: "10px 14px 10px 18px",
		backgroundColor: accent ? alpha(accent, 0.06) : SURFACE,
		border: `1px ${dashed ? "dashed" : "solid"} ${accent ? alpha(accent, 0.35) : dashed ? NEUTRAL[400] : alpha(theme.palette.primary.main, 0.08)}`,
		boxShadow: dashed ? "none" : undefined,
		// Clips the accent hairline to the rounded corners.
		overflow: accentColor ? "hidden" : undefined,
		cursor: clickable ? "pointer" : "default",
		userSelect: "none",
		transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}, background-color ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}, box-shadow ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}`,
		"@media (prefers-reduced-motion: reduce)": { transition: "none", animation: entranceIndex !== undefined ? "none" : undefined },
		...(entranceIndex !== undefined && {
			"@keyframes clickableStatTileEnter": {
				from: { opacity: 0, transform: `translateY(${STAT_TILE.ENTRANCE_RISE_PX}px)` },
				to: { opacity: 1, transform: "translateY(0)" },
			},
			animation: `clickableStatTileEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT} backwards`,
			animationDelay: `${Math.min(entranceIndex * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX)}ms`,
		}),
		"&::after": accentColor
			? { content: '""', position: "absolute", insetInline: 0, top: 0, height: ACCENT_STRIP_HEIGHT, backgroundColor: accentColor }
			: undefined,
		outline: "none",
		"&::before": {
			content: '""',
			position: "absolute",
			top: 8,
			bottom: 8,
			left: 0,
			width: STAT_TILE.ACCENT_RAIL_WIDTH,
			borderRadius: `0 ${STAT_TILE.ACCENT_RAIL_WIDTH}px ${STAT_TILE.ACCENT_RAIL_WIDTH}px 0`,
			backgroundColor: accent ?? "transparent",
			transition: `background-color ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}`,
		},
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover": clickable
				? {
						borderColor: accent ? alpha(accent, 0.55) : alpha(theme.palette.primary.main, 0.24),
						boxShadow: `0px 2px 8px 0px ${alpha(theme.palette.primary.main, 0.1)}, 0px 1px 3px 0px ${alpha(NEUTRAL[600], 0.06)}`,
						transform: "translateY(-1px)",
					}
				: undefined,
		},
		"&:active": clickable ? { transform: "scale(0.97)" } : undefined,
		"&:focus-visible": clickable
			? {
					borderColor: theme.palette.primary.main,
					boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
				}
			: undefined,
	});
